import { useParams, Link } from 'react-router-dom';
import { useSharedCollection } from '../hooks/useDeepLink';
import { useAuth } from '../hooks/useAuth';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

export function SharedCollectionPage() {
  const { token } = useParams<{ token: string }>();
  const { data: collection, loading, error } = useSharedCollection(token);
  const { user } = useAuth();

  // Set document meta tags
  useDocumentMeta({
    title: collection ? `${collection.name} | Read l8r Collection` : 'Shared Collection | Read l8r',
    description: collection?.description || `A collection of ${collection?.articleCount || 0} articles`,
    ogTitle: collection?.name,
    ogDescription: collection?.description || `${collection?.articleCount || 0} curated articles`,
    ogType: 'website',
    ogUrl: token ? `${window.location.origin}/c/${token}` : undefined,
    twitterCard: 'summary'
  });

  if (loading) {
    return (
      <div className="shared-page loading">
        <div className="spinner" />
        <p>Loading shared collection...</p>
        <style>{sharedStyles}</style>
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="shared-page error">
        <div className="error-icon">📚</div>
        <h1>Collection Not Found</h1>
        <p>{error || 'This shared collection could not be found.'}</p>
        <Link to="/" className="btn-primary">Go to Read l8r</Link>
        <style>{sharedStyles}</style>
      </div>
    );
  }

  return (
    <div className="shared-page">
      {/* Header */}
      <header className="shared-header">
        <Link to="/" className="shared-logo">
          <span>📖</span> Read l8r
        </Link>
        <div className="shared-actions">
          {user ? (
            <button className="btn-primary" onClick={() => handleSaveCollection()}>
              Save Collection
            </button>
          ) : (
            <Link to="/app" className="btn-primary">
              Sign up to save
            </Link>
          )}
        </div>
      </header>

      {/* Collection info */}
      <div className="collection-header">
        <div className="collection-icon">📚</div>
        <h1>{collection.name}</h1>
        {collection.description && (
          <p className="collection-description">{collection.description}</p>
        )}
        <div className="collection-meta">
          <span>{collection.articleCount} articles</span>
          {collection.sharedBy && (
            <>
              <span>•</span>
              <span>Shared by {collection.sharedBy.name}</span>
            </>
          )}
        </div>
      </div>

      {/* Article list */}
      <div className="collection-articles">
        {collection.articles.map((article, i) => (
          <div key={article.id} className="collection-article-card">
            <div className="article-number">{i + 1}</div>
            <div className="article-info">
              <h3>{article.title}</h3>
              {article.excerpt && <p className="article-excerpt">{article.excerpt}</p>}
              <div className="article-card-meta">
                <span>{article.wordCount.toLocaleString()} words</span>
                {article.url && (
                  <a href={article.url} target="_blank" rel="noopener noreferrer">
                    View source ↗
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="shared-cta">
        <h2>Create your own collections</h2>
        <p>Organize your reading with Read l8r's smart collections.</p>
        <Link to="/app" className="btn-primary btn-large">
          Get Started Free
        </Link>
      </div>

      <style>{sharedStyles}</style>
    </div>
  );

  async function handleSaveCollection() {
    alert('Save collection coming soon!');
  }
}

const sharedStyles = `
  .shared-page {
    min-height: 100vh;
    background: var(--landing-bg, #fafaf9);
    color: var(--landing-text, #1c1917);
  }

  @media (prefers-color-scheme: dark) {
    .shared-page {
      background: #0a0a0a;
      color: #fafaf9;
    }
  }

  .shared-page.loading,
  .shared-page.error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 2rem;
    text-align: center;
  }

  .error-icon {
    font-size: 4rem;
    opacity: 0.5;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(249, 115, 22, 0.2);
    border-top-color: #f97316;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .shared-header {
    position: sticky;
    top: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    background: rgba(250, 250, 249, 0.9);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--landing-border, #e7e5e4);
  }

  @media (prefers-color-scheme: dark) {
    .shared-header {
      background: rgba(10, 10, 10, 0.9);
      border-color: #27272a;
    }
  }

  .shared-logo {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 700;
    font-size: 1.25rem;
    color: inherit;
    text-decoration: none;
  }

  .collection-header {
    text-align: center;
    padding: 3rem 1.5rem;
    background: linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(251, 146, 60, 0.05) 100%);
  }

  .collection-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
  }

  .collection-header h1 {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .collection-description {
    color: var(--landing-text-secondary, #78716c);
    max-width: 600px;
    margin: 0 auto 1rem;
  }

  .collection-meta {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: var(--landing-text-secondary, #78716c);
  }

  .collection-articles {
    max-width: 800px;
    margin: 0 auto;
    padding: 2rem 1.5rem;
  }

  .collection-article-card {
    display: flex;
    gap: 1rem;
    padding: 1.5rem;
    background: var(--landing-surface, #ffffff);
    border: 1px solid var(--landing-border, #e7e5e4);
    border-radius: 12px;
    margin-bottom: 1rem;
    transition: transform 0.2s, box-shadow 0.2s;
  }

  @media (prefers-color-scheme: dark) {
    .collection-article-card {
      background: #141414;
      border-color: #27272a;
    }
  }

  .collection-article-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }

  .article-number {
    width: 32px;
    height: 32px;
    background: rgba(249, 115, 22, 0.1);
    color: #f97316;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  .article-info {
    flex: 1;
    min-width: 0;
  }

  .article-info h3 {
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .article-excerpt {
    font-size: 0.875rem;
    color: var(--landing-text-secondary, #78716c);
    line-height: 1.5;
    margin-bottom: 0.5rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .article-card-meta {
    display: flex;
    gap: 1rem;
    font-size: 0.75rem;
    color: var(--landing-text-secondary, #78716c);
  }

  .article-card-meta a {
    color: #f97316;
    text-decoration: none;
  }

  .article-card-meta a:hover {
    text-decoration: underline;
  }

  .shared-cta {
    text-align: center;
    padding: 4rem 1.5rem;
    background: linear-gradient(135deg, #f97316 0%, #fb923c 100%);
    color: white;
  }

  .shared-cta h2 {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .shared-cta p {
    opacity: 0.9;
    margin-bottom: 1.5rem;
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: #f97316;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    text-decoration: none;
    border: none;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-primary:hover {
    background: #ea580c;
  }

  .shared-cta .btn-primary {
    background: white;
    color: #f97316;
  }

  .btn-large {
    padding: 1rem 2rem;
    font-size: 1.125rem;
  }
`;
